import { action, makeObservable, runInAction } from 'mobx';
import { EmailCodeStore, FlowStart } from './emailCodeStore';
import { RootStore } from './root';
import { ROUTES } from '../routes';
import { SIGNUP_EMAIL_STORAGE_KEY } from './constants';
import { ERROR_CODES } from '../utils/errorHandler';

/** Registering with a code sent to the user's email instead of a password. */
export class SignupEmailFactorStore extends EmailCodeStore {
  constructor(rootStore: RootStore) {
    super(rootStore);
    makeObservable<this, 'finish'>(this, {
      finish: action,
      start: action.bound,
      backToEmail: action.bound,
      switchToPassword: action.bound,
    });
    if (rootStore.navigationStore.currentRoute === ROUTES.SIGNUP_EMAIL_CODE) {
      this.email = rootStore.userDataStore.email;
      this.backToEmail();
    }
  }

  enableAccess() {
    return (
      Boolean(this.email) &&
      this.rootStore.signupStore.isSignupWithEmailCodeEnabled
    );
  }

  protected get errorFlow() {
    return 'signup' as const;
  }

  /**
   * Sends the first code and opens the code screen. Returns the error code
   * when nothing was sent, so the caller can show it on the signup form.
   */
  async start(email: string) {
    if (this.isLoading) {
      return undefined;
    }
    if (this.email !== email) {
      this.reset();
      this.email = email;
    }
    try {
      sessionStorage.setItem(SIGNUP_EMAIL_STORAGE_KEY, email);
    } catch (_) {}
    if (!this.stateToken && !this.hasSentCode) {
      const version = this.requestVersion;
      const sendError = await this.resendCode({
        lockOnFailure: false,
        // Signup goes on with a password for it (signupWithEmailCode).
        unreportedErrors: [ERROR_CODES.UNSUPPORTED_REGISTRATION_FACTOR],
      });
      if (this.isStale(version)) {
        // Abandoned meanwhile (another email, or left the flow): nothing to show.
        return undefined;
      }
      if (sendError) {
        // Nothing was sent, so do not open a screen that claims otherwise.
        return sendError;
      }
      if (this.isCompleting) {
        return undefined;
      }
    }
    runInAction(() => {
      this.rootStore.signupStore.isEmailSignupMode = false;
    });
    this.rootStore.navigationStore.navigate(ROUTES.SIGNUP_EMAIL_CODE);
    return undefined;
  }

  /** A deferred registration: it issues a challenge without creating the account. */
  protected startFlow(): Promise<FlowStart> {
    return this.rootStore.signupStore.requestEmailCode(this.email);
  }

  protected finish(
    payload: Parameters<RootStore['navigationStore']['postSignup']>[0],
  ) {
    this.isCompleting = true;
    try {
      sessionStorage.removeItem(SIGNUP_EMAIL_STORAGE_KEY);
    } catch (_) {}
    this.rootStore.navigationStore.postSignup(payload);
  }

  /** On the password form, where a signup can go on without the code. */
  protected askForCaptcha(errorCode: string) {
    this.switchToPassword();
    this.rootStore.signupStore.pendingCaptcha.request(errorCode);
  }

  switchToPassword() {
    if (this.isLoading) {
      return;
    }
    const { signupStore, userDataStore, navigationStore } = this.rootStore;
    userDataStore.setEmail(this.email);
    signupStore.onInitFields();
    signupStore.emailRepeatField.setValue(this.email);
    signupStore.prefersPassword = true;
    signupStore.isEmailSignupMode = true;
    // True for "Choose a Password", or a CAPTCHA that a resend asked for, on
    // the code screen. False anywhere else: a CAPTCHA that a send from the
    // email-only step asked for, or a backend that doesn't register by code.
    signupStore.shouldGoBackToCodeScreen =
      navigationStore.currentRoute === ROUTES.SIGNUP_EMAIL_CODE;
    navigationStore.navigate(ROUTES.SIGNUP_PASSWORD);
  }

  backToEmail() {
    const { signupStore, emailStepStore, userDataStore, navigationStore } =
      this.rootStore;
    const email =
      (navigationStore.currentRoute === ROUTES.SIGNUP_PASSWORD &&
        signupStore.emailField.value) ||
      this.email ||
      userDataStore.email;
    this.reset();
    userDataStore.setEmail(email);
    signupStore.onInitFields();
    signupStore.prefersPassword = false;
    signupStore.isEmailSignupMode = false;
    signupStore.shouldGoBackToCodeScreen = false;
    emailStepStore.emailField.setValue(email);
    emailStepStore.isFetchingByDefaultEmail = false;
    navigationStore.navigate(ROUTES.SIGNUP_PASSWORD);
  }

  protected onReset() {
    try {
      sessionStorage.removeItem(SIGNUP_EMAIL_STORAGE_KEY);
    } catch (_) {}
  }
}
